/**
 * Página Generar — Generador de Memorias Técnicas.
 * Usa índices FAISS pre-construidos por categoría (Consultoría / Data e IA / Gemelo Digital).
 */

import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Alert,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  LinearProgress,
  CircularProgress,
  Stack,
  Chip,
  Divider,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Download as DownloadIcon,
  CheckCircle as CheckCircleIcon,
  Build as BuildIcon,
  Storage as StorageIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';

import FileUpload from '../components/FileUpload';
import { SectionHeader, Badge } from '../components/quantia';
import {
  generateOffer,
  getIndexStatus,
  buildIndex,
  downloadTextFile,
} from '../services/api';
import { COLORS } from '../theme/theme';

const CATEGORIAS = ['Consultoría', 'Data e IA', 'Gemelo Digital'];

const paperSx = {
  p: { xs: 2.5, md: 4 },
  mb: 3,
  borderRadius: 4,
  border: '1px solid rgba(91, 79, 240, 0.08)',
  backgroundColor: '#FFFFFF',
};
const stepHeaderSx = {
  fontFamily: "'Fira Sans', sans-serif",
  fontWeight: 800,
  fontSize: '1.125rem',
  color: COLORS.black,
  mb: 0.5,
};
const stepDescSx = { color: COLORS.gray, fontSize: '0.875rem', mb: 2.5 };

const Generar = () => {
  const [files, setFiles] = useState([]);
  const [categoria, setCategoria] = useState('Consultoría');
  const [nombreLicitacion, setNombreLicitacion] = useState('');
  const [tipoOferta, setTipoOferta] = useState('Técnica');
  const [requisitosEspecificos, setRequisitosEspecificos] = useState('');

  const [indexStatus, setIndexStatus] = useState({});   // { cat: { index_built, num_docs } }
  const [isBuilding, setIsBuilding] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  const [borrador, setBorrador] = useState('');
  const [docsUsados, setDocsUsados] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Cargar estado de índices al montar y cuando cambia categoría
  useEffect(() => {
    loadIndexStatus();
  }, []);

  const loadIndexStatus = async () => {
    try {
      const data = await getIndexStatus();
      setIndexStatus(data.status || {});
    } catch {
      // silencioso
    }
  };

  const currentIndex = indexStatus[categoria] || { index_built: false, num_docs: 0 };

  // ------------------------------------------------------------------
  // Construir índice
  // ------------------------------------------------------------------
  const handleBuildIndex = async () => {
    setIsBuilding(true);
    setError('');
    setSuccess('');
    try {
      const result = await buildIndex(categoria);
      setSuccess(
        `Índice construido: ${result.num_docs} memorias → ${result.num_chunks} fragmentos indexados.`,
      );
      await loadIndexStatus();
    } catch (err) {
      setError(err.message || 'Error al construir el índice');
    } finally {
      setIsBuilding(false);
    }
  };

  // ------------------------------------------------------------------
  // Generar memoria técnica
  // ------------------------------------------------------------------
  const handleGenerate = async () => {
    if (files.length === 0) {
      setError('Sube al menos un PDF de la licitación');
      return;
    }
    if (!nombreLicitacion.trim()) {
      setError('Introduce el nombre o número de la licitación');
      return;
    }
    if (!currentIndex.index_built) {
      setError(`El índice de "${categoria}" no está construido. Pulsa "Construir índice" primero.`);
      return;
    }

    setIsGenerating(true);
    setError('');
    setSuccess('');
    setBorrador('');
    setDocsUsados([]);

    try {
      const response = await generateOffer({
        files,
        nombre_licitacion: nombreLicitacion,
        tipo_oferta: tipoOferta,
        categoria,
        requisitos_especificos: requisitosEspecificos,
      });

      if (response.success) {
        setBorrador(response.borrador);
        setDocsUsados(response.documentos_similares || []);
        setSuccess('Memoria técnica generada. Revisa y completa las secciones marcadas con [COMPLETAR].');
      } else {
        setError('No se pudo generar la memoria técnica');
      }
    } catch (err) {
      setError(err.message || 'Error al generar la memoria técnica');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownload = (format) => {
    if (!borrador) return;
    const ts = Date.now();
    downloadTextFile(
      borrador,
      `memoria_tecnica_${nombreLicitacion}_${ts}.${format}`,
      format === 'md' ? 'text/markdown' : 'text/plain',
    );
  };

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------
  return (
    <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
      <SectionHeader
        label="03 — Generación"
        title="Generador de Memorias Técnicas"
        highlight="Memorias Técnicas"
        description="Selecciona la categoría, construye el índice de tus memorias de referencia una vez, y genera borradores personalizados para cada nueva licitación."
        sx={{ mb: 5 }}
      />

      {/* ── Paso 0: Categoría + estado del índice ── */}
      <Paper sx={{ ...paperSx, backgroundColor: 'rgba(91, 79, 240, 0.04)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <StorageIcon sx={{ color: COLORS.primary }} />
          <Typography sx={stepHeaderSx}>Conocimiento de referencia (índice FAISS)</Typography>
        </Box>
        <Typography sx={stepDescSx}>
          Las memorias técnicas anteriores de Quantia se indexan una sola vez por categoría.
          El índice se guarda en disco y se reutiliza en todas las generaciones.
        </Typography>

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={5}>
            <FormControl fullWidth size="small">
              <InputLabel>Categoría</InputLabel>
              <Select
                value={categoria}
                label="Categoría"
                onChange={(e) => {
                  setCategoria(e.target.value);
                  setError('');
                  setBorrador('');
                }}
              >
                {CATEGORIAS.map((c) => (
                  <MenuItem key={c} value={c}>{c}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={4}>
            {currentIndex.index_built ? (
              <Chip
                icon={<CheckCircleIcon />}
                label={`Índice listo · ${currentIndex.num_docs} memorias`}
                color="success"
                variant="outlined"
              />
            ) : (
              <Chip
                icon={<WarningIcon />}
                label={`Sin indexar · ${currentIndex.num_docs} memorias disponibles`}
                color="warning"
                variant="outlined"
              />
            )}
          </Grid>

          <Grid item xs={12} sm={3}>
            <Button
              variant={currentIndex.index_built ? 'outlined' : 'contained'}
              color="primary"
              fullWidth
              startIcon={isBuilding ? <CircularProgress size={16} color="inherit" /> : <BuildIcon />}
              onClick={handleBuildIndex}
              disabled={isBuilding || currentIndex.num_docs === 0}
            >
              {isBuilding
                ? 'Indexando…'
                : currentIndex.index_built
                ? 'Reconstruir'
                : 'Construir índice'}
            </Button>
          </Grid>
        </Grid>

        {isBuilding && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ color: COLORS.gray, mb: 1 }}>
              Generando embeddings de las memorias de {categoria}… puede tardar 1-2 minutos.
            </Typography>
            <LinearProgress />
          </Box>
        )}
      </Paper>

      <Divider sx={{ mb: 3 }} />

      {/* ── Paso 1: PDFs de la licitación ── */}
      <Paper sx={paperSx}>
        <Typography sx={stepHeaderSx}>📤 Paso 1 · Documentos de la licitación</Typography>
        <Typography sx={stepDescSx}>
          Sube los pliegos de la nueva licitación (PCAP y/o PPT).
        </Typography>
        <FileUpload
          onFilesChange={(f) => { setFiles(f); setError(''); setBorrador(''); }}
          acceptedTypes=".pdf"
          multiple
          label="Selecciona los PDFs de la licitación"
        />
      </Paper>

      {/* ── Paso 2: Configurar ── */}
      <Paper sx={paperSx}>
        <Typography sx={stepHeaderSx}>⚙️ Paso 2 · Configurar la memoria técnica</Typography>
        <Typography sx={stepDescSx}>
          Define el alcance de la oferta.
        </Typography>

        <Grid container spacing={2.5}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Nombre / Número de la licitación"
              placeholder="Ej: EXP-2024-001"
              value={nombreLicitacion}
              onChange={(e) => setNombreLicitacion(e.target.value)}
              required
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Tipo de oferta</InputLabel>
              <Select
                value={tipoOferta}
                label="Tipo de oferta"
                onChange={(e) => setTipoOferta(e.target.value)}
              >
                <MenuItem value="Técnica">Técnica</MenuItem>
                <MenuItem value="Económica">Económica</MenuItem>
                <MenuItem value="Técnica + Económica">Técnica + Económica</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Requisitos o aspectos específicos (opcional)"
              placeholder="Ej: enfatizar experiencia en gemelo digital de agua, incluir metodología BIM…"
              value={requisitosEspecificos}
              onChange={(e) => setRequisitosEspecificos(e.target.value)}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Mensajes */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && !borrador && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Botón generar */}
      {files.length > 0 && nombreLicitacion && (
        <Box sx={{ mb: 3 }}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            fullWidth
            startIcon={isGenerating ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}
            onClick={handleGenerate}
            disabled={isGenerating}
          >
            {isGenerating ? 'Generando memoria técnica…' : '🚀 Generar memoria técnica'}
          </Button>
        </Box>
      )}

      {/* Progreso */}
      {isGenerating && (
        <Paper sx={paperSx}>
          <Typography sx={stepHeaderSx}>⏳ Generando…</Typography>
          <Stack spacing={0.75} sx={{ pl: 1, mb: 2 }}>
            <Typography variant="body2" sx={{ color: COLORS.gray }}>
              • Extrayendo texto de los pliegos
            </Typography>
            <Typography variant="body2" sx={{ color: COLORS.gray }}>
              • Buscando secciones similares en el índice de {categoria}
            </Typography>
            <Typography variant="body2" sx={{ color: COLORS.gray }}>
              • Generando borrador con GPT-4o
            </Typography>
          </Stack>
          <LinearProgress />
        </Paper>
      )}

      {/* Resultado */}
      {borrador && (
        <Paper sx={paperSx}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Typography sx={stepHeaderSx}>📄 Memoria técnica generada</Typography>
            <Badge variant="success" icon={<CheckCircleIcon sx={{ fontSize: 12 }} />}>
              Generada con éxito
            </Badge>
          </Box>

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>
          )}

          {docsUsados.length > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" gutterBottom>
                <strong>Memorias de referencia usadas ({categoria}):</strong>
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 1 }}>
                {docsUsados.map((doc, i) => (
                  <Chip key={i} label={doc} size="small" variant="outlined" color="primary" />
                ))}
              </Stack>
            </Alert>
          )}

          <Box
            sx={{
              borderRadius: 3,
              border: '1px solid rgba(91,79,240,0.10)',
              backgroundColor: '#FAFAFA',
              p: 2.5,
              mb: 3,
              maxHeight: 600,
              overflow: 'auto',
            }}
          >
            <Typography
              component="pre"
              sx={{
                whiteSpace: 'pre-wrap',
                fontFamily: "'Courier New', monospace",
                fontSize: '0.875rem',
                color: COLORS.textPrimary,
                margin: 0,
              }}
            >
              {borrador}
            </Typography>
          </Box>

          <Alert severity="warning" sx={{ mb: 2 }}>
            Revisa y completa las secciones marcadas con <code>[COMPLETAR]</code>.
          </Alert>

          <Typography sx={{ ...stepHeaderSx, mb: 1.5 }}>💾 Descargar borrador</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Button variant="contained" color="primary" fullWidth startIcon={<DownloadIcon />} onClick={() => handleDownload('txt')}>
                Descargar TXT
              </Button>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button variant="outlined" color="primary" fullWidth startIcon={<DownloadIcon />} onClick={() => handleDownload('md')}>
                Descargar Markdown
              </Button>
            </Grid>
          </Grid>
        </Paper>
      )}
    </Container>
  );
};

export default Generar;
